import { RocketOutlined } from '@ant-design/icons';
import { Col, ConfigProvider, Modal, Row, Typography } from 'antd';
import { HookAPI } from 'antd/es/modal/useModal';
import { PropsWithChildren, createContext, memo, useCallback, useContext } from 'react';
import { Trans } from 'react-i18next';
import useSubscriptionLimits from '../../hooks/subscription/useSubscriptionLimits';
import GrispiLoadingLayout from '../../layouts/GrispiLoadingLayout';
import { PRIMARY_COLOR } from '../../styles/theme';
import { getCurrentUser, isAdmin } from '../../utils/accountHelper';
import { pushTo } from '../../utils/routeHelper';

export type SubscriptionLimitsDataType = {
    tier: string;
    features: {
        SUPPORT_AUTOMATION: boolean;
        SUPPORT_BULK_CHANGE: boolean;
        SUPPORT_CALL: boolean;
        SUPPORT_CHAT: boolean;
        SUPPORT_COMMENT_TRANSLATE: boolean;
        SUPPORT_CUSTOM_FILTERS: boolean;
        SUPPORT_FILTER_EXPORT: boolean;
        SUPPORT_GDS_EXPORT: boolean;
        SUPPORT_GSOCIAL: boolean;
        SUPPORT_IMPORT: boolean;
        SUPPORT_IP_RESTRICTION: boolean;
        SUPPORT_MACRO: boolean;
        SUPPORT_MULTIPLE_FORMS: boolean;
        SUPPORT_PUBLIC_API: boolean;
        SUPPORT_RESTRICTED_FIELD: boolean;
        SUPPORT_SCHEDULED_REMINDER_EMAIL: boolean;
        SUPPORT_SURVEY: boolean;
        SUPPORT_TRIGGERS: boolean;
    };
    resourceSizeLimit: {
        ENABLED_AUTOMATION_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_CUSTOMER_FORM_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_CUSTOM_FILTER_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_FORM_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_MACRO_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_ORGANIZATION_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_TEAM_MEMBER_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_TICKET_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_TRIGGER_COUNT: { totalLimit: number; remainingLimit: number };
        ENABLED_USER_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_AUTOMATION_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_CUSTOMER_FORM_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_CUSTOM_FILTER_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_FORM_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_MACRO_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_ORGANIZATION_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_TICKET_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_TRIGGER_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_UNCLOSED_TICKET_COUNT: { totalLimit: number; remainingLimit: number };
        TOTAL_USER_CUSTOM_FIELD_COUNT: { totalLimit: number; remainingLimit: number };
    };
    attachmentSizeLimit: {
        TOTAL_ATTACHMENT_SIZE_PER_TENANT: {
            totalLimit: number;
            remainingLimit: number;
        };
        TOTAL_ATTACHMENT_SIZE_PER_AGENT: {
            totalLimit: number;
            remainingLimit: number;
        };
        TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_AGENT: {
            totalLimit: number;
            remainingLimit: number;
        };
        TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_AGENT: {
            totalLimit: number;
            remainingLimit: number;
        };
        TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_CUSTOMER: {
            totalLimit: number;
            remainingLimit: number;
        };
        TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_CUSTOMER: {
            totalLimit: number;
            remainingLimit: number;
        };
        ATTACHMENT_SIZE_PER_FILE_FOR_AGENT: {
            totalLimit: number;
            remainingLimit: number;
        };
        ATTACHMENT_SIZE_PER_FILE_FOR_CUSTOMER: {
            totalLimit: number;
            remainingLimit: number;
        };
    };
};

export enum subscriptionTierMap {
    READ_ONLY,
    TRIAL,
    LEVEL_1,
    LEVEL_2,
    LEVEL_3
}
export enum subscriptionLimitMap {
    ENABLED_AUTOMATION_COUNT = 'ENABLED_AUTOMATION_COUNT',
    ENABLED_CUSTOMER_FORM_COUNT = 'ENABLED_CUSTOMER_FORM_COUNT',
    ENABLED_CUSTOM_FILTER_COUNT = 'ENABLED_CUSTOM_FILTER_COUNT',
    ENABLED_FORM_COUNT = 'ENABLED_FORM_COUNT',
    ENABLED_MACRO_COUNT = 'ENABLED_MACRO_COUNT',
    ENABLED_ORGANIZATION_CUSTOM_FIELD_COUNT = 'ENABLED_ORGANIZATION_CUSTOM_FIELD_COUNT',
    ENABLED_TEAM_MEMBER_COUNT = 'ENABLED_TEAM_MEMBER_COUNT',
    ENABLED_TICKET_CUSTOM_FIELD_COUNT = 'ENABLED_TICKET_CUSTOM_FIELD_COUNT',
    ENABLED_TRIGGER_COUNT = 'ENABLED_TRIGGER_COUNT',
    ENABLED_USER_CUSTOM_FIELD_COUNT = 'ENABLED_USER_CUSTOM_FIELD_COUNT',
    TOTAL_AUTOMATION_COUNT = 'TOTAL_AUTOMATION_COUNT',
    TOTAL_CUSTOMER_FORM_COUNT = 'TOTAL_CUSTOMER_FORM_COUNT',
    TOTAL_CUSTOM_FILTER_COUNT = 'TOTAL_CUSTOM_FILTER_COUNT',
    TOTAL_FORM_COUNT = 'TOTAL_FORM_COUNT',
    TOTAL_MACRO_COUNT = 'TOTAL_MACRO_COUNT',
    TOTAL_ORGANIZATION_CUSTOM_FIELD_COUNT = 'TOTAL_ORGANIZATION_CUSTOM_FIELD_COUNT',
    TOTAL_TICKET_CUSTOM_FIELD_COUNT = 'TOTAL_TICKET_CUSTOM_FIELD_COUNT',
    TOTAL_TRIGGER_COUNT = 'TOTAL_TRIGGER_COUNT',
    TOTAL_UNCLOSED_TICKET_COUNT = 'TOTAL_UNCLOSED_TICKET_COUNT',
    TOTAL_USER_CUSTOM_FIELD_COUNT = 'TOTAL_USER_CUSTOM_FIELD_COUNT'
}

export enum subscriptionFeatureMap {
    SUPPORT_AUTOMATION = 'SUPPORT_AUTOMATION',
    SUPPORT_BULK_CHANGE = 'SUPPORT_BULK_CHANGE',
    SUPPORT_CALL = 'SUPPORT_CALL',
    SUPPORT_CHAT = 'SUPPORT_CHAT',
    SUPPORT_COMMENT_TRANSLATE = 'SUPPORT_COMMENT_TRANSLATE',
    SUPPORT_CUSTOM_FILTERS = 'SUPPORT_CUSTOM_FILTERS',
    SUPPORT_FILTER_EXPORT = 'SUPPORT_FILTER_EXPORT',
    SUPPORT_GDS_EXPORT = 'SUPPORT_GDS_EXPORT',
    SUPPORT_GSOCIAL = 'SUPPORT_GSOCIAL',
    SUPPORT_IMPORT = 'SUPPORT_IMPORT',
    SUPPORT_IP_RESTRICTION = 'SUPPORT_IP_RESTRICTION',
    SUPPORT_MACRO = 'SUPPORT_MACRO',
    SUPPORT_MULTIPLE_FORMS = 'SUPPORT_MULTIPLE_FORMS',
    SUPPORT_PUBLIC_API = 'SUPPORT_PUBLIC_API',
    SUPPORT_RESTRICTED_FIELD = 'SUPPORT_RESTRICTED_FIELD',
    SUPPORT_SCHEDULED_REMINDER_EMAIL = 'SUPPORT_SCHEDULED_REMINDER_EMAIL',
    SUPPORT_SURVEY = 'SUPPORT_SURVEY',
    SUPPORT_TRIGGERS = 'SUPPORT_TRIGGERS'
}
export enum subscriptionAttachmentLimitMap {
    ATTACHMENT_SIZE_PER_FILE_FOR_AGENT = 'ATTACHMENT_SIZE_PER_FILE_FOR_AGENT',
    ATTACHMENT_SIZE_PER_FILE_FOR_CUSTOMER = 'ATTACHMENT_SIZE_PER_FILE_FOR_CUSTOMER',
    TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_AGENT = 'TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_AGENT',
    TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_CUSTOMER = 'TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_CUSTOMER',
    TOTAL_ATTACHMENT_SIZE_PER_AGENT = 'TOTAL_ATTACHMENT_SIZE_PER_AGENT',
    TOTAL_ATTACHMENT_SIZE_PER_TENANT = 'TOTAL_ATTACHMENT_SIZE_PER_TENANT',
    TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_AGENT = 'TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_AGENT',
    TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_CUSTOMER = 'TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_CUSTOMER'
}

const SubscriptionContext = createContext<SubscriptionLimitsDataType>({
    tier: null,
    features: null,
    attachmentSizeLimit: null,
    resourceSizeLimit: null
});

/**
 * resource limits
 * ENABLED_AUTOMATION_COUNT => çalışmıyor
 * TOTAL_AUTOMATION_COUNT => çalışmıyor
 * ENABLED_MACRO_COUNT => enabled form count la karışmış.
 * MAX_USER_COUNT => bir işe yaramıyor. sadece db de var limitler arasında gelmiyor.
 */

/**
 * bulk change meta datasini automation dan aliyoruz eger bulk change varsa ama otomasyon yoksa sorun cikiyor.
 */

/**
 * feature check
 * macro tamam
 * triggers tamam
 * survey tamam
 * import tamam
 * filter export tamam
 *
 * ---bunlar nasil kontrol edilecek bilmiyorum.
 * SUPPORT_SCHEDULED_REMINDER_EMAIL hesap ayarlarinda uyari goster
 * SUPPORT_RESTRICTED_FIELD ignore
 * SUPPORT_IP_RESTRICTION istek atinca 402 gelsin
 * SUPPORT_GSOCIAL token isteginden 402 gelcek
 * SUPPORT_PUBLIC_API ignore
 * SUPPORT_GDS_EXPORT ignore
 * SUPPORT_COMMENT_TRANSLATE plugin api tarafindan engellenecek
 * SUPPORT_CHAT plugin api tarafindan engellenecek
 * SUPPORT_CALL plugin api tarafindan engellenecek
 *---
 *
 * SUPPORT_MULTIPLE_FORMS editlenebiliyor, disable edilebiliyor, silinebiliyor. enable edilemiyor. olusturulamiyor.
 * SUPPORT_CUSTOM_FILTERS editlenebiliyor, disable edilebiliyor, silinebiliyor. enable edilebiliyor. olusturulamiyor.
 *
 *
 * beklemede
 * SUPPORT_AUTOMATION
 * SUPPORT_BULK_CHANGE
 */

/**
 * user form gonderirken hatayi yakalamiyoruz
 *
 * TOTAL_ATTACHMENT_SIZE_PER_TENANT yanlis
 * ATTACHMENT_SIZE_PER_FILE_FOR_AGENT yanlis
 * TOTAL_ATTACHMENT_SIZE_PER_TICKET_FOR_AGENT yanlis
 * TOTAL_ATTACHMENT_COUNT_PER_TICKET_FOR_AGENT yanlis
 *
 */

export const useSubscription = () => {
    const subContext = useContext(SubscriptionContext);
    const currentSubscriptionTier = subscriptionTierMap[subContext?.tier];
    const subscriptionLimitCheck = useCallback(
        (targetLimit: subscriptionLimitMap) => {
            if (currentSubscriptionTier !== subscriptionTierMap.READ_ONLY) {
                if (subContext?.resourceSizeLimit[targetLimit]?.totalLimit > 0) return true;
                else return false;
            } else {
                return false;
            }
        },
        [currentSubscriptionTier, subContext?.resourceSizeLimit]
    );
    // ! This function deprecated, use feature check instead of tier check
    const subscriptionTierCheck = useCallback(
        (targetSubscriptionTier: subscriptionTierMap) => {
            if (currentSubscriptionTier >= targetSubscriptionTier) return true;
            else return false;
        },
        [currentSubscriptionTier]
    );
    const subscriptionFeatureCheck = useCallback(
        (targetSubscriptionFeature: subscriptionFeatureMap) => {
            return subContext?.features[targetSubscriptionFeature];
        },
        [subContext?.features]
    );
    return { subscriptionTierCheck, subscriptionLimitCheck, subscriptionFeatureCheck };
};

let dynamicSubscriptionModal: HookAPI;

const SubscriptionController = ({ children }: PropsWithChildren) => {
    const [dynamicSubscriptionModalApi, dynamicSubscriptionModalContextHolder] = Modal.useModal();
    dynamicSubscriptionModal = dynamicSubscriptionModalApi;

    const { data: subscriptionLimits, isLoading } = useSubscriptionLimits();

    if (isLoading) return <GrispiLoadingLayout />;

    return (
        <SubscriptionContext.Provider value={subscriptionLimits}>
            {children}
            <ConfigProvider
                theme={{
                    token: {
                        colorWarning: PRIMARY_COLOR
                    }
                }}
            >
                {dynamicSubscriptionModalContextHolder}
            </ConfigProvider>
        </SubscriptionContext.Provider>
    );
};

export const grispiSubscriptionModal = ({
    limit,
    feature
}: {
    feature?: subscriptionFeatureMap;
    limit?: subscriptionLimitMap;
}) => {
    const isCurrentUserAdmin = isAdmin(getCurrentUser().role);
    const title = <Trans ns="subscription" i18nKey="modalTitle" />;
    const icon = <RocketOutlined />;

    const content = (
        <Col>
            <Row>
                <Typography.Title level={4}>
                    {limit ? (
                        <Trans ns="subscription" i18nKey={`limitTitle.${limit}`} />
                    ) : (
                        <Trans ns="subscription" i18nKey={`featureTitle.${feature}`} />
                    )}
                </Typography.Title>
            </Row>
            <Row>
                <Typography.Text>
                    {limit ? (
                        <Trans ns="subscription" i18nKey={`limitTitle.${limit}`} />
                    ) : (
                        <Trans ns="subscription" i18nKey={`featureTitle.${feature}`} />
                    )}
                    {limit ? (
                        <Trans ns="subscription" i18nKey={`limitExceeded.${isCurrentUserAdmin ? 'admin' : 'agent'}`} />
                    ) : (
                        <Trans
                            ns="subscription"
                            i18nKey={`featureNotSupported.${isCurrentUserAdmin ? 'admin' : 'agent'}`}
                        />
                    )}
                </Typography.Text>
            </Row>
        </Col>
    );
    if (isCurrentUserAdmin)
        return dynamicSubscriptionModal.confirm({
            title,
            content,
            icon,
            centered: true,
            cancelText: <Trans ns="subscription" i18nKey="manageSubscription" />,
            onCancel() {
                pushTo('/admin/subscription');
            }
        });

    return dynamicSubscriptionModal.info({
        title,
        content,
        icon,
        centered: true
    });
};

export const useSubscriptionContext = () => useContext(SubscriptionContext);
export default memo(SubscriptionController);
